import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, MenuItem, Slider, TextField, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFilterOptions,
  fetchHrManagementData,
} from "../store/reducers/riskAnalysisSlice";

const MIN_DATE = "2021-07-01";
const ALL_OPTION = "Alle";
const todayStr = () => new Date().toISOString().slice(0, 10);

const roundScore = (value) => Number(value.toFixed(2));

const HR_CONFIGS = {
  dnk: {
    title: "DNK HR Management",
    filterLabels: {
      first: "Instruktør",
      second: "KLR",
      third: "Sted",
    },
    filterKeys: {
      first: ["instructor", "instruktor"],
      second: ["klr"],
      third: ["location", "sted", "beach"],
    },
    optionSourceKeys: {
      first: ["instructor_id", "instructor"],
      second: ["klr_id", "klr"],
      third: ["beach_location", "location", "sted", "beach"],
    },
    yAxis: {
      title: "Middelværdi",
      domain: [0, 2],
      ticks: [0, 0.5, 1, 1.5, 2],
    },
    secondSeries: {
      key: "klr_value",
      label: "Valgte KLR",
      color: "#a8d9ef",
    },
  },
  gentofte: {
    title: "Gentofte LIFE HR Management",
    filterLabels: {
      first: "Instruktør",
      second: "Livredder",
      third: "Sted",
    },
    filterKeys: {
      first: ["instructor", "instruktor"],
      second: ["lifeguard", "livredder", "employee"],
      third: ["location", "sted", "beach"],
    },
    optionSourceKeys: {
      first: ["instructor_id", "instructor"],
      second: ["lifeguard_id", "livredder_id", "employee_id", "lifeguard"],
      third: ["beach_location", "location", "sted", "beach"],
    },
    yAxis: {
      title: "Middelværdi",
      domain: [0, 5],
      ticks: [0, 1, 2, 3, 4, 5],
    },
    secondSeries: {
      key: "lifeguard_value",
      fallbackKeys: ["lifegurd_value"],
      label: "Valgte livredder",
      color: "#a8d9ef",
    },
  },
};

const getRowHour = (row) => {
  const timeValue =
    row?.time ||
    row?.utc_scanned?.split?.(" ")?.[1] ||
    row?.utc_tid_skannet?.split?.(" ")?.[1] ||
    "";
  const hour = Number(String(timeValue).split(":")[0]);
  return Number.isFinite(hour) ? hour : null;
};

const getRowDimension = (row, keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value);
    }
  }
  return "";
};

const buildOptions = (rows, keys) => {
  const values = Array.from(
    new Set(rows.map((row) => getRowDimension(row, keys)).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  return [ALL_OPTION, ...values];
};

const normalizeOptionValues = (values) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value ?? "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

const getFilterOptionValues = (filtersData, candidateKeys = []) => {
  for (const key of candidateKeys) {
    if (Array.isArray(filtersData?.[key])) {
      return normalizeOptionValues(filtersData[key]);
    }
  }
  return [];
};

const toApiValue = (value) => (value === ALL_OPTION ? "All" : value);

const getRowMetric = (row, primaryKey, fallbackKeys = []) => {
  const keys = [primaryKey, ...fallbackKeys];
  for (const key of keys) {
    if (key && Object.prototype.hasOwnProperty.call(row, key)) {
      return Number(row?.[key] ?? 0);
    }
  }
  return null;
};

const aggregateChartRows = (rows, secondaryKey, secondaryFallbackKeys = []) => {
  const grouped = rows.reduce((acc, row) => {
    const key = String(row?.name ?? "").trim();
    if (!key) return acc;
    if (!acc[key]) {
      acc[key] = { name: row.name, total: 0, count: 0, secondaryTotal: 0, secondaryCount: 0 };
    }
    acc[key].total += Number(row?.value ?? 0);
    acc[key].count += 1;
    const secondaryValue = getRowMetric(row, secondaryKey, secondaryFallbackKeys);
    if (secondaryValue !== null) {
      acc[key].secondaryTotal += secondaryValue;
      acc[key].secondaryCount += 1;
    }
    return acc;
  }, {});

  return Object.values(grouped).map((item) => ({
    name: item.name,
    value: roundScore(item.total / item.count),
    ...(secondaryKey && item.secondaryCount
      ? { [secondaryKey]: roundScore(item.secondaryTotal / item.secondaryCount) }
      : {}),
  }));
};

const HRManagementChartView = ({ config, organisationId }) => {
  const dispatch = useDispatch();
  const hrFilters = useSelector((state) => state.riskAnalysis.hrFilters);
  const hrManagement = useSelector((state) => state.riskAnalysis.hrManagement);
  const sourceRows = hrManagement.data ?? [];
  const [dateFrom, setDateFrom] = useState(MIN_DATE);
  const [dateTo, setDateTo] = useState(todayStr());
  const [timeRange, setTimeRange] = useState([0, 24]);
  const [firstFilter, setFirstFilter] = useState(ALL_OPTION);
  const [secondFilter, setSecondFilter] = useState(ALL_OPTION);
  const [thirdFilter, setThirdFilter] = useState(ALL_OPTION);
  const filterOptionsError = hrFilters.error;
  const hasTouchedDateFromRef = useRef(false);
  const hasTouchedDateToRef = useRef(false);

  useEffect(() => {
    const orgId = Number(organisationId);
    if (Number.isFinite(orgId) && orgId > 0) {
      dispatch(fetchFilterOptions(orgId));
    }
  }, [dispatch, organisationId]);

  useEffect(() => {
    const orgId = Number(organisationId);
    if (!Number.isFinite(orgId) || orgId <= 0) return;

    const requestParams = {
      organizationId: orgId,
      timeRange,
    };

    if (hasTouchedDateFromRef.current && String(dateFrom).trim() !== "") {
      requestParams.dateFrom = dateFrom;
    }

    if (hasTouchedDateToRef.current && String(dateTo).trim() !== "") {
      requestParams.dateTo = dateTo;
    }

    const instructor = toApiValue(firstFilter);
    const location = toApiValue(thirdFilter);
    const secondFilterValue = toApiValue(secondFilter);

    if (instructor !== "All") {
      requestParams.instructor = instructor;
    }

    if (location !== "All") {
      requestParams.location = location;
    }

    if (orgId === 2 && secondFilterValue !== "All") {
      requestParams.klr = secondFilterValue;
    }

    if (orgId === 19 && secondFilterValue !== "All") {
      requestParams.lifeguard = secondFilterValue;
    }

    dispatch(
      fetchHrManagementData(requestParams)
    );
  }, [dateFrom, dateTo, dispatch, firstFilter, organisationId, secondFilter, thirdFilter, timeRange]);

  const firstApiOptions = useMemo(
    () => getFilterOptionValues(hrFilters.data, config.optionSourceKeys?.first),
    [config.optionSourceKeys, hrFilters.data]
  );
  const secondApiOptions = useMemo(
    () => getFilterOptionValues(hrFilters.data, config.optionSourceKeys?.second),
    [config.optionSourceKeys, hrFilters.data]
  );
  const thirdApiOptions = useMemo(
    () => getFilterOptionValues(hrFilters.data, config.optionSourceKeys?.third),
    [config.optionSourceKeys, hrFilters.data]
  );

  const firstOptions = useMemo(
    () => (firstApiOptions.length ? [ALL_OPTION, ...firstApiOptions] : buildOptions(sourceRows, config.filterKeys.first)),
    [config.filterKeys.first, firstApiOptions, sourceRows]
  );
  const secondOptions = useMemo(
    () => (secondApiOptions.length ? [ALL_OPTION, ...secondApiOptions] : buildOptions(sourceRows, config.filterKeys.second)),
    [config.filterKeys.second, secondApiOptions, sourceRows]
  );
  const thirdOptions = useMemo(
    () => (thirdApiOptions.length ? [ALL_OPTION, ...thirdApiOptions] : buildOptions(sourceRows, config.filterKeys.third)),
    [config.filterKeys.third, thirdApiOptions, sourceRows]
  );

  useEffect(() => {
    if (!firstOptions.includes(firstFilter)) {
      setFirstFilter(ALL_OPTION);
    }
  }, [firstFilter, firstOptions]);

  useEffect(() => {
    if (!secondOptions.includes(secondFilter)) {
      setSecondFilter(ALL_OPTION);
    }
  }, [secondFilter, secondOptions]);

  useEffect(() => {
    if (!thirdOptions.includes(thirdFilter)) {
      setThirdFilter(ALL_OPTION);
    }
  }, [thirdFilter, thirdOptions]);

  const filteredData = useMemo(
    () =>
      sourceRows.filter((row) => {
        const rowHour = getRowHour(row);
        if (rowHour !== null && (rowHour < timeRange[0] || rowHour > timeRange[1])) {
          return false;
        }
        return true;
      }),
    [sourceRows, timeRange]
  );

  const chartData = useMemo(
    () =>
      aggregateChartRows(
        filteredData,
        config.secondSeries?.key,
        config.secondSeries?.fallbackKeys
      ),
    [config.secondSeries?.fallbackKeys, config.secondSeries?.key, filteredData]
  );

  const hasSecondarySeries = useMemo(
    () =>
      Boolean(
      config.secondSeries?.key &&
          chartData.some((row) => Object.prototype.hasOwnProperty.call(row, config.secondSeries.key))
    ),
    [chartData, config.secondSeries]
  );

  useEffect(() => {
    console.log(`${config.title} filtered data`, {
      filters: {
        dateFrom,
        dateTo,
        timeRange,
        [config.filterLabels.first]: firstFilter,
        [config.filterLabels.second]: secondFilter,
        [config.filterLabels.third]: thirdFilter,
      },
      filteredData,
    });
  }, [
    config.filterLabels.first,
    config.filterLabels.second,
    config.filterLabels.third,
    config.title,
    dateFrom,
    dateTo,
    filteredData,
    firstFilter,
    secondFilter,
    thirdFilter,
    timeRange,
  ]);

  const sliderMarks = useMemo(
    () =>
      Array.from({ length: 9 }, (_, index) => ({
        value: index * 3,
        label: String(index * 3),
      })),
    []
  );

  return (
    <Box sx={{ mt: 3, border: "1px solid #ddd", borderRadius: 2, p: 3 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 4, color: "#2d2d2d" }}>
        {config.title}
      </Typography>

      <Box sx={{ borderTop: "1px solid #bbb", pt: 4 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.6fr 1fr 1fr" },
            gap: 4,
            alignItems: "start",
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Vælg tidsperiode:
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "stretch",
                border: "1px solid #cfcfcf",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <TextField
                type="date"
                value={dateFrom}
                onChange={(event) => {
                  hasTouchedDateFromRef.current = true;
                  setDateFrom(event.target.value);
                }}
                variant="standard"
                fullWidth
                InputProps={{ disableUnderline: true }}
                inputProps={{
                  min: MIN_DATE,
                  max: todayStr(),
                  style: { padding: "16px 18px", textAlign: "center", fontSize: 18 },
                }}
              />
              <Box
                sx={{
                  px: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderLeft: "1px solid #cfcfcf",
                  borderRight: "1px solid #cfcfcf",
                  bgcolor: "#f2f2f2",
                  fontSize: 18,
                  color: "#666",
                }}
              >
                to
              </Box>
              <TextField
                type="date"
                value={dateTo}
                onChange={(event) => {
                  hasTouchedDateToRef.current = true;
                  setDateTo(event.target.value);
                }}
                variant="standard"
                fullWidth
                InputProps={{ disableUnderline: true }}
                inputProps={{
                  min: MIN_DATE,
                  max: todayStr(),
                  style: { padding: "16px 18px", textAlign: "center", fontSize: 18 },
                }}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {config.filterLabels.first}
            </Typography>
            <TextField select fullWidth value={firstFilter} onChange={(event) => setFirstFilter(event.target.value)}>
              {firstOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {config.filterLabels.second}
            </Typography>
            <TextField select fullWidth value={secondFilter} onChange={(event) => setSecondFilter(event.target.value)}>
              {secondOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.6fr 1fr 1fr" },
            gap: 4,
            alignItems: "start",
            mt: 6,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Vælg tidspunkt på døgnet:
            </Typography>
            <Slider
              value={timeRange}
              onChange={(_, value) => setTimeRange(value)}
              valueLabelDisplay="on"
              min={0}
              max={24}
              marks={sliderMarks}
              sx={{
                mt: 5,
                color: "#2c69aa",
                "& .MuiSlider-valueLabel": {
                  backgroundColor: "#2c69aa",
                  borderRadius: 1,
                },
              }}
            />
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {config.filterLabels.third}
            </Typography>
            <TextField select fullWidth value={thirdFilter} onChange={(event) => setThirdFilter(event.target.value)}>
              {thirdOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
        {filterOptionsError ? (
          <Typography sx={{ mt: 2, color: "#a94442", fontSize: 13 }}>
            {filterOptionsError}
          </Typography>
        ) : null}
        {hrManagement.error ? (
          <Typography sx={{ mt: 1, color: "#a94442", fontSize: 13 }}>
            {String(hrManagement.error)}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ mt: 8, width: "100%", height: 620 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 32, right: 24, left: 24, bottom: 120 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#ececec" />
            <XAxis
              dataKey="name"
              angle={-90}
              textAnchor="end"
              interval={0}
              height={165}
              tick={{ fontSize: 14, fill: "#2c69aa" }}
            />
            <YAxis
              domain={config.yAxis.domain}
              ticks={config.yAxis.ticks}
              label={{
                value: config.yAxis.title,
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle", fill: "#222", fontSize: 16, fontWeight: 600 },
              }}
              tick={{ fontSize: 14, fill: "#2c69aa" }}
            />
            <Tooltip formatter={(value) => [value, config.yAxis.title]} />
            {hasSecondarySeries ? <Legend /> : null}
            <Bar dataKey="value" name={ALL_OPTION} fill="#3e6fa9" radius={[2, 2, 0, 0]} maxBarSize={42}>
              <LabelList
                dataKey="value"
                position="top"
                formatter={(value) => Number(value).toFixed(1).replace(/\.0$/, "")}
                style={{ fill: "#2c69aa", fontSize: 13, fontWeight: 700 }}
              />
            </Bar>
            {hasSecondarySeries ? (
              <Bar
                dataKey={config.secondSeries.key}
                name={config.secondSeries.label}
                fill={config.secondSeries.color}
                radius={[2, 2, 0, 0]}
                maxBarSize={42}
              >
                <LabelList
                  dataKey={config.secondSeries.key}
                  position="top"
                  formatter={(value) =>
                    value === undefined || value === null
                      ? ""
                      : Number(value).toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1")
                  }
                  style={{ fill: "#2c69aa", fontSize: 13, fontWeight: 700 }}
                />
              </Bar>
            ) : null}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

const resolveHRConfig = ({ organisationId, customerId }) => {
  if (Number(organisationId) === 2 || String(customerId) === "17") {
    return HR_CONFIGS.dnk;
  }

  if (Number(organisationId) === 19 || String(customerId) === "144") {
    return HR_CONFIGS.gentofte;
  }

  return null;
};

const HRManagementDashboard = ({ organisationId, customerId }) => {
  const config = useMemo(
    () => resolveHRConfig({ organisationId, customerId }),
    [organisationId, customerId]
  );

  if (!config) {
    return (
      <Box sx={{ mt: 3, p: 3, border: "1px solid #ddd", borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          HR Management
        </Typography>
        <Typography sx={{ mt: 1, color: "#666" }}>
          No HR management configuration found for this organisation.
        </Typography>
      </Box>
    );
  }

  return <HRManagementChartView config={config} organisationId={organisationId} />;
};

export default HRManagementDashboard;
